import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, useReducedMotion } from "motion/react";
import { useLenis } from "lenis/react";
import { ArrowLeft, ArrowRight, X } from "@phosphor-icons/react";

/* Album image. `size="small"` uses only the 800 file (grids). Large views get a srcset up to 1600. */
export function AlbumImg({ photo, size = "large", sizes = "100vw", priority = false, className = "", style }) {
  const small = size === "small";
  return (
    <img
      className={className}
      src={small ? photo.srcSmall : photo.src}
      srcSet={small ? undefined : `${photo.srcSmall} 800w, ${photo.src} 1600w`}
      sizes={small ? undefined : sizes}
      alt={photo.alt}
      width={photo.width}
      height={photo.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      fetchPriority={priority ? "high" : undefined}
      style={style}
    />
  );
}

/*
  Video budget. Every ClipVideo registers here with its visible ratio.
  Only the two most visible videos (ratio > 0.2) play. Everything else is paused.
*/
const MAX_PLAYING = 2;
const registry = new Map();

function schedule() {
  const winners = [...registry.entries()]
    .filter(([, ratio]) => ratio > 0.2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, MAX_PLAYING)
    .map(([element]) => element);
  registry.forEach((_, element) => {
    if (winners.includes(element)) {
      if (element.paused) {
        element.muted = true;
        element.play().catch(() => {});
      }
    } else if (!element.paused) {
      element.pause();
    }
  });
}

let observer = null;
function getObserver() {
  if (observer || typeof IntersectionObserver === "undefined") return observer;
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (registry.has(entry.target)) registry.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
      });
      schedule();
    },
    { threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.75, 1] },
  );
  return observer;
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) registry.forEach((_, element) => element.pause());
    else schedule();
  });
}

/* Silent looping clip. Reduced motion shows the poster only. */
export function ClipVideo({ clip, className = "", label, eager = false }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const element = ref.current;
    const io = getObserver();
    if (!element || !io || reduceMotion) return undefined;
    element.muted = true;
    registry.set(element, 0);
    io.observe(element);
    return () => {
      io.unobserve(element);
      registry.delete(element);
      element.pause();
      schedule();
    };
  }, [reduceMotion]);

  if (reduceMotion) {
    return <img className={className} src={clip.poster} alt={label ?? clip.alt} width={clip.width} height={clip.height} loading={eager ? "eager" : "lazy"} decoding="async" />;
  }
  return (
    <video
      ref={ref}
      className={className}
      poster={clip.poster}
      muted
      loop
      playsInline
      preload={eager ? "auto" : "none"}
      aria-label={label ?? clip.alt}
      width={clip.width}
      height={clip.height}
    >
      <source src={clip.src} type="video/mp4" />
    </video>
  );
}

/* Full-screen photo viewer. Arrow keys, Escape, swipe, focus kept inside. */
export function Lightbox({ photos, index, onClose, onStep }) {
  const reduceMotion = useReducedMotion();
  const lenis = useLenis();
  const closeRef = useRef(null);
  const touch = useRef(null);
  const photo = photos[index];

  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();
    lenis?.stop();
    const overflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      lenis?.start();
      document.documentElement.style.overflow = overflow;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [lenis]);

  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onStep(1);
      if (event.key === "ArrowLeft") onStep(-1);
      if (event.key === "Tab") {
        const nodes = [...document.querySelectorAll(".pg-lb button")];
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onStep]);

  if (!photo) return null;
  return createPortal(
    <motion.div
      className="pg-lb"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      initial={reduceMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={reduceMotion ? undefined : { opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
      onTouchStart={(event) => { touch.current = event.touches[0].clientX; }}
      onTouchEnd={(event) => {
        if (touch.current === null) return;
        const dx = event.changedTouches[0].clientX - touch.current;
        if (Math.abs(dx) > 50) onStep(dx < 0 ? 1 : -1);
        touch.current = null;
      }}
    >
      <div className="pg-lb__bar">
        <span className="pg-lb__count">{index + 1} / {photos.length}</span>
        <button type="button" className="pg-lb__btn" ref={closeRef} onClick={onClose} aria-label="Close photo viewer">
          <X weight="bold" aria-hidden="true" />
        </button>
      </div>
      <figure className="pg-lb__figure">
        <motion.img
          key={photo.id}
          src={photo.src}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          initial={reduceMotion ? false : { opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        />
        <figcaption>{photo.caption}</figcaption>
      </figure>
      <div className="pg-lb__nav">
        <button type="button" className="pg-lb__btn" onClick={() => onStep(-1)} aria-label="Previous photo">
          <ArrowLeft weight="bold" aria-hidden="true" />
        </button>
        <button type="button" className="pg-lb__btn" onClick={() => onStep(1)} aria-label="Next photo">
          <ArrowRight weight="bold" aria-hidden="true" />
        </button>
      </div>
    </motion.div>,
    document.body,
  );
}
